import { useMutation } from '@apollo/client';
import { Grid, Icon, Paper, Tooltip } from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { Module } from '@thro-types/modules-selections';

import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import DISCHARGE_STUDENT_MUTATION from 'gql/mutations/ModuleSelectionsService/dischargeStudent.gql';
import QUERY_LOTTERY_RESULT from 'gql/queries/ModuleSelectionsService/getLatestCalculatedLotteryResultForStudent.gql';

import { invalidateModulesQuery, useModulesQuery } from 'api/hooks/modules';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import { SelectionState } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import {
  getCurrentSelectionState,
  userDataToLecturerOption
} from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { LatestCalculatedLotteryResultsForStudent } from 'types/AppTypes';

import { ModuleStudentSelection } from '../../ModuleSelection';

interface WaitingListProps {
  moduleSelection: ModuleStudentSelection;
  latestLottery: LatestCalculatedLotteryResultsForStudent;
}
export const WaitingList = ({ moduleSelection, latestLottery }: WaitingListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  //TODO fetch only the for the student required Modules
  const { data: modules, isLoading } = useModulesQuery({
    params: {
      selectionId: moduleSelection.id
    }
  });

  const [dischargeStudent] = useMutation(DISCHARGE_STUDENT_MUTATION, {
    context: { clientName: 'federation' },
    refetchQueries: [QUERY_LOTTERY_RESULT]
  });

  const [currentlyEditingModule, setCurrentlyEditingModule] = useState<any>();
  const [signOutFromCourseDialogOpen, setSignOutFromCourseDialogOpen] = useState(false);

  const getRows = (latestLottery: LatestCalculatedLotteryResultsForStudent, modules: Module[]) => {
    let studentInWaitlist = false;
    const rows: any[] = [];
    for (const result of latestLottery.resultWithPosition) {
      if (!result?.assignedToModule && !result.modulePreference.studentDischarge) {
        studentInWaitlist = true;
        const studentModule = modules.find(item => item.id === result.modulePreference?.moduleId);
        rows.push({
          ...studentModule,
          resultId: result.id,
          modulePreferenceId: result.modulePreference.id,
          positionInWaitingList: result.positionInWaitingList
        });
      }
    }
    return studentInWaitlist ? rows : [];
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.pages.studentView.signOut())}
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            setSignOutFromCourseDialogOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [t]
  );

  const waitingListColumns = useMemo((): GridColDef[] => {
    return [
      {
        flex: 3,
        minWidth: isMobile ? 50 : 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: isMobile ? 80 : 200,
        headerName: t(translations.pages.modules.lecturer()),
        field: 'moduleOfficerUser',
        renderCell: params => (
          <Tooltip title={userDataToLecturerOption(params.row.moduleOfficerUser, t).label}>
            <span>{userDataToLecturerOption(params.row.moduleOfficerUser, t).label}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: isMobile ? 60 : 200,
        headerName: t(translations.pages.studentView.numberOfParticipants()),
        field: 'numberOfActiveParticipants',
        renderCell: params => (
          <Tooltip title={params.row.numberOfActiveParticipants}>
            <span>{params.row.numberOfActiveParticipants}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: isMobile ? 60 : 200,
        headerName: t(translations.pages.studentView.positionInWaitingList()),
        field: 'positionInWatingList',
        renderCell: params => (
          <Tooltip title={params.row.positionInWaitingList}>
            <span>{params.row.positionInWaitingList}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: isMobile ? 80 : 200,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  const waitingListColumnsReadOnly = useMemo((): GridColDef[] => {
    return [
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.lecturer()),
        field: 'moduleOfficerUser',
        renderCell: params => (
          <Tooltip title={userDataToLecturerOption(params.row.moduleOfficerUser, t).label}>
            <span>{userDataToLecturerOption(params.row.moduleOfficerUser, t).label}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.studentView.numberOfParticipants()),
        field: 'numberOfActiveParticipants',
        renderCell: params => (
          <Tooltip title={params.row.numberOfActiveParticipants}>
            <span>{params.row.numberOfActiveParticipants}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.studentView.positionInWaitingList()),
        field: 'positionInWatingList',
        renderCell: params => (
          <Tooltip title={params.row.positionInWaitingList}>
            <span>{params.row.positionInWaitingList}</span>
          </Tooltip>
        )
      }
    ];
  }, [t]);

  const handleSignOutFromCourse = async () => {
    const { errors } = await dischargeStudent({
      variables: {
        modulePreferenceId: parseInt(currentlyEditingModule?.modulePreferenceId, 10)
      }
    });
    invalidateModulesQuery(queryClient);
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.dischargeStudentFailed())));
    } else {
      dispatch(
        AlertActions.success(t(translations.pages.modules.alerts.dischargeStudentSuccessful()))
      );
    }
  };

  return (
    <>
      <ConfirmationDialog
        open={signOutFromCourseDialogOpen}
        title={t(translations.pages.studentView.signOutDialogTitle())}
        onClose={() => {
          setSignOutFromCourseDialogOpen(false);
        }}
        onConfirm={() => {
          setSignOutFromCourseDialogOpen(false);
          handleSignOutFromCourse();
        }}
      >
        <div>{t(translations.pages.studentView.signOutDialog())}</div>
      </ConfirmationDialog>
      <Grid
        item
        xs={12}
        container
        direction="column"
        style={{ height: isMobile ? '60vh' : '40vh', width: '100vw' }}
        component={Paper}
      >
        <DataGrid
          style={{ height: '100%' }}
          disableColumnFilter
          disableRowSelectionOnClick
          getRowId={item => item.resultId}
          columns={
            getCurrentSelectionState(moduleSelection) !== SelectionState.ReadOnly
              ? waitingListColumns
              : waitingListColumnsReadOnly
          }
          rows={
            (getCurrentSelectionState(moduleSelection) === SelectionState.SignOutPossible ||
              getCurrentSelectionState(moduleSelection) === SelectionState.ReadOnly) &&
            !isLoading
              ? getRows(latestLottery, modules!)
              : []
          }
          paginationMode={'client'}
          checkboxSelection={false}
          columnVisibilityModel={{ numberOfActiveParticipants: !isMobile }}
        />
      </Grid>
    </>
  );
};
